/*eslint no-console: 0*/
import PropTypes from "prop-types";
import React from "react";
import { defineMessages, injectIntl } from "react-intl";
import Relay from "react-relay";
import config from "../../common/config";
import types from "../../common/model-api/types";
import { safeJSONparse } from "../../common/util/safeJSONparse";
import InquiryModal from "../app/inquiryModal.js";
import { getLastOrganisationId } from "../app/LastOrganisationId";
import { wrapNotFound } from "../app/NotFoundHandler.react";
import { getUISettings } from '../app/UISettings.react.js';
import urlMapping from "../app/URLMapping";
import ChannelIcon from "../channel-defs/ChannelIcon.react.js";
import FlowSimpleList from "../flows/FlowSimpleList.react";
import AppPanel from "../fw/AppPanel.react";
import SafeHTML from "../fw/SafeHTML.react.js";
import CustomAccordion from "./Accordion.react";
import './ShoptetApplicationDetail.scss';
import TooltipButton from "./Tooltip.react.js";
import { User } from '../../common/types/types';

let scenarioMap = {
    "shoptet": {
        "flexibee": {
            "invoice": "transfer-invoices-from-shoptet-to-flexibee",
            "invoice-manual": "manual-invoices-shoptet-to-abra-flexi",
            "order": "shoptet-to-flexibee"
        }
    },
    "upgates": {
        "flexibee": {
            "invoice": "transfer-upgates-api-e-shop-invoices-to-flexibee",
            "invoice-manual": "manual-invoices-upgates-to-abra-flexi",
            "order": "upgates-api-to-flexibee"
        },
        "pohoda": {
            "invoice": "transfer-upgates-api-e-shop-invoices-to-pohoda",
            "order": "upgates-api-to-pohoda"
        },
        "money-s3": {
            "invoice": "transfer-upgates-api-e-shop-invoices-to-money-s3",
            "order": "upgates-api-to-money-s3"
        },
        "abra-gen": {
            "invoice": "transfer-upgates-api-e-shop-invoices-to-abra-gen",
            "order": "upgates-api-to-abra-gen"
        }
    },
    "eshop-rychle": {
        "flexibee": {
            "invoice": "transfer-eshop-rychle-api-engine-e-shop-invoices-to-flexibee",
            "order": "eshop-rychle-api-engine-to-flexibee"//"eshop-rychle-to-flexibee"
        },
        "pohoda": {
            "invoice": "transfer-eshop-rychle-api-engine-e-shop-invoices-to-pohoda",
            "order": "eshop-rychle-api-engine-to-pohoda"//"eshop-rychle-to-pohoda"
        },
        "money-s3": {
            "invoice": "transfer-eshop-rychle-api-engine-e-shop-invoices-to-money-s3",
            "order": "eshop-rychle-api-engine-to-money-s3"//"eshop-rychle-to-money-s3"
        },
        "abra-gen": {
            "invoice": "transfer-eshop-rychle-api-engine-e-shop-invoices-to-abra-gen",
            "order": "eshop-rychle-api-engine-to-abra-gen"//"eshop-rychle-to-abra-gen"
        },
            "peppos": {
                "invoice": "transfer-invoices-from-eshop-rychle-api-engine-to-peppos", //pridat spravny scenar pro eshop-rychle-api-engine-to-peppos
        }
    }
};

const NO_ADDON_SCENARIO_URL_FRAGMENT = "__no-addon-scenario__";

const addonPlatforms = {
    "shoptet": {
        name: "Shoptet",
        icon: "shoptet-sign",
        addonParam: "isShoptetAddon=true",
    },
    "upgates": {
        name: "Upgates",
        icon: "upgates-sign",
        addonParam: "isUpgatesAddon=true",
    },
    "eshop-rychle": {
        name: "Eshop-rychle",
        icon: "eshop-rychle",
        addonParam: "isEshopRychleAddon=true",
    },
};

let getAddonPlatform = (appName) => {
    const fallbackName = appName || "Addon";
    return addonPlatforms[appName?.toLowerCase()] || {
        name: fallbackName,
        icon: fallbackName,
        addonParam: "",
    };
};

// Test addon uses erp format 'test-{erp}' (e.g. 'test-flexibee').
// Extracts the actual ERP identifier for scenario selection.
let resolveErp = (erp) => {
    if (erp?.startsWith('test-')) return erp.slice(5);
    return erp;
};

let getScenarioUrl = (appName, erp, type) => {
    const scenarios = scenarioMap[appName?.toLowerCase()]?.[resolveErp(erp)?.toLowerCase()];
    return scenarios?.[type] || "";
};

let getErpFromUrl = () => {
    let fixedSearchParams = "?" + window?.location?.search.replaceAll("?", "&");
    const searchParams = new URLSearchParams(fixedSearchParams);
    return searchParams.get('erp');
};

let getErpIconFromUrl = () => {
    const erp = getErpFromUrl();
    if (erp === "pohoda" || erp === "money-s3") {
        return erp + "-sign";
    }
    return erp;
};

const messages = defineMessages({
    transferInvoices: {id: 'applicationAddonDetail.transferInvoices', 'defaultMessage': 'We only want to automatically transfer invoices to the accounting'},
    transferInvoicesPricing: {id: 'applicationAddonDetail.transferInvoicesPricingExVat', 'defaultMessage': 'Monthly operation {price} CZK excluding VAT'},
    transferInvoicesByCountPricing: {id: 'applicationAddonDetail.transferInvoicesByCountPricing', 'defaultMessage': 'Monthly from {price} CZK excluding VAT depending on the number of invoices transferred.'},
    manualTransferInvoices: {id: 'applicationAddonDetail.manualTransferInvoices', 'defaultMessage': 'We only want to manually export invoices to the accountant once a month'},
    manualTransferInvoicesPricing: {id: 'applicationAddonDetail.manualTransferInvoicesPricing', 'defaultMessage': 'No monthly payment (free)'},

    transferOrders: {id: 'applicationAddonDetail.transferOrders', 'defaultMessage': 'We want to transfer orders and ship from {erp}'},
    transferOrdersPricing: {id: 'applicationAddonDetail.transferOrdersPricingExVat', 'defaultMessage': '<Link href="https://www.dativery.com/cs/users/price/#objednavky">Monthly from {price} CZK excluding VAT</Link> depending on the number of orders transferred.'},
    transferOrders2: {id: 'applicationAddonDetail.transferOrders2', 'defaultMessage': 'We want to transfer orders and ship from {appName}'},
    transferOrders2Pricing: {id: 'applicationAddonDetail.transferOrders2PricingExVat', 'defaultMessage': '<Link href="https://www.dativery.com/cs/users/price/#objednavky">Monthly from {price} CZK excluding VAT</Link> depending on the number of orders transferred.'},
    needHelp: {id: 'applicationAddonDetail.needHelp', 'defaultMessage': 'I need help'},
    needHelpDescription: {id: 'applicationAddonDetail.needHelpDescription', 'defaultMessage': 'If you are not sure which form of addon or installation to choose, consult with us.'},
    needHelpPricing: {id: 'applicationAddonDetail.needHelpPricing', 'defaultMessage': 'Free'},
    proposeSolution: {id: 'applicationAddonDetail.proposeSolution', 'defaultMessage': 'We want to connect the e-shop with {erp}, and we need to design a solution.'},
    proposeSolutionDescription: {id: 'applicationAddonDetail.proposeSolutionDescription', 'defaultMessage': 'We have an idea of the process of processing orders and we need to select and set up services Dativery according to it.'},
    proposeSolutionSolution: {id: 'applicationAddonDetail.proposeSolutionSolution', 'defaultMessage': 'You tell the consultant your idea and requirements, he will perform the installation and detailed setup of the integration and check its operation.'},
    proposeSolutionPricing: {id: 'applicationAddonDetail.proposeSolutionPricing', 'defaultMessage': 'Depending on the selected solution and price offer'},
    alone: {id: 'applicationAddonDetail.alone', 'defaultMessage': 'I will install it myself'},
    aloneDescription: {id: 'applicationAddonDetail.aloneDescription', 'defaultMessage': 'In case of any minor uncertainties, you can consult with our support or schedule a consultation with a specialist (2000 CZK/hour).'},
    alonePricing: {id: 'applicationAddonDetail.alonePricing', 'defaultMessage': 'Free'},
    withDativery: {id: 'applicationAddonDetail.withDativery', 'defaultMessage': 'With Dativery'},
    withDativeryPricing: {id: 'applicationAddonDetail.withDativeryPricing', 'defaultMessage': '4190'},
    withDativeryDescription: {id: 'applicationAddonDetail.withDativeryPricingDescription', 'defaultMessage': 'You tell the consultant your idea and requirements, he will perform the installation and detailed setup of the integration and check its operation.'},
    addonSettings: {id: 'applicationAddonDetail.addonSettings', 'defaultMessage': 'Addon settings'},
    welcome: {id: 'applicationAddonDetail.welcome', 'defaultMessage': 'Welcome in Dativery!'},
    whatDoYouExpect: {id: 'applicationAddonDetail.whatDoYouExpect', 'defaultMessage': 'What do you expect from the connection of {appName} and {erp}?'},
    welcomeDescription: {id: 'applicationAddonDetail.welcomeDescription', 'defaultMessage': 'The following guide will help you with the selection of the right variant of the connection of {appName} and {erp} and the form of its installation.'},
    price: {id: 'applicationAddonDetail.price', 'defaultMessage': 'Price'},
    solution: {id: 'applicationAddonDetail.solution', 'defaultMessage': 'Solution'},
    title: {id: 'applicationAddonDetail.title', 'defaultMessage': 'Addon settings'},
    noPrice: {id: 'applicationAddonDetail.noPrice', 'defaultMessage': 'Free'},
});

let getScenarioNode = (scenarioConnection) => {
    return scenarioConnection?.edges?.[0]?.node;
};

let parseScenarioPrice = (value) => {
    const parsed = parseFloat(value || 0);
    return isNaN(parsed) ? 0 : parsed;
};

let getScenarioInstallationPrice = (scenario) => {
    return parseScenarioPrice(scenario?.priceInstallation);
};

let getScenarioPriceText = (scenario) => {
    return typeof scenario?.priceText === "string" && scenario.priceText.trim() ? scenario.priceText : null;
};

let getScenarioMonthlyPrice = (scenario) => {
    const pricePerInvocation = parseScenarioPrice(scenario?.pricePerInvocation);
    const pricePerInstance = parseScenarioPrice(scenario?.pricePerInstance);
    if (pricePerInvocation > 0) {
        return {price: pricePerInvocation, isInvocationBased: true};
    }
    return {price: pricePerInstance, isInvocationBased: false};
};

let getAddonPricingFromScenario = (scenario, monthlyMessage, invocationMonthlyMessage, html = false) => {
    const monthlyPrice = getScenarioMonthlyPrice(scenario);
    return {
        withDativeryPricing: getScenarioInstallationPrice(scenario),
        priceText: getScenarioPriceText(scenario),
        monthlyPrice: monthlyPrice.price,
        monthlyMessage: monthlyPrice.isInvocationBased ? invocationMonthlyMessage : monthlyMessage,
        html,
    };
};

let renderAddonPricing = (intl, pricing) => {
    if (pricing.priceText) {
        return pricing.priceText;
    }
    if (!pricing.monthlyPrice) {
        return "";
    }

    const value = intl.formatMessage(pricing.monthlyMessage, {
        price: intl.formatNumber(pricing.monthlyPrice, {maximumFractionDigits: 0}),
    });
    return pricing.html ? <SafeHTML value={value} /> : value;
};

const varMessages = defineMessages({
    manualTransferInvoicesDescription: {id: 'applicationAddonDetail.manualTransferInvoicesDescription', 'defaultMessage': 'We process everything in {appName}, we only need to export invoices once a month to accountant for {erp}.'},
    manualTransferInvoicesSolution: {id: 'applicationAddonDetail.manualTransferInvoicesSolution', 'defaultMessage': 'Integration <a href="/s/{url}/">Export invoices from {appName} to {erp}.</a>'},
    transferInvoicesDescription: {id: 'applicationAddonDetail.transferInvoicesDescription', 'defaultMessage': 'We process orders and shipment of goods in {appName}, we only need to transfer invoices to {erp} for accounting processing.'},
    transferInvoicesSolution: {id: 'applicationAddonDetail.transferInvoicesSolution', 'defaultMessage': 'Integration <a href="/s/{url}/">Transfer of invoices from {appName} to {erp}.</a>'},
    transferOrdersDescription: {id: 'applicationAddonDetail.transferOrdersDescription', 'defaultMessage': 'We receive orders in {appName}, but we need to process them in {erp}, from where we ship the goods.'},
    transferOrdersSolution: {id: 'applicationAddonDetail.transferOrdersSolution', 'defaultMessage': 'Choose integration <a href="/s/{url}/">Connection {appName} and {erp} for order transfer, product and warehouse status.</a>'},
    transferOrders2Description: {id: 'applicationAddonDetail.transferOrders2Description', 'defaultMessage': 'We receive, process and ship orders in {appName}, we need to transfer them to {erp} together with invoices.'},
    transferOrders2Solution: {id: 'applicationAddonDetail.transferOrders2Solution', 'defaultMessage': 'Choose integration <a href="/s/{url}/">Connection {appName} and {erp} for order transfer, product and warehouse status.</a>'}
});

interface ShoptetApplicationDetailProps {
    app?: string;
    intl?: object;
    relay?: object;
    user: User;
}

class ShoptetApplicationDetail extends React.Component<ShoptetApplicationDetailProps> {
    typeName = "channels";

    static propTypes = {
        app: PropTypes.string,
        intl: PropTypes.object,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired,
    };
    static contextTypes = {
        router: PropTypes.object,
    };

    constructor() {
        super();
        this.state = {
            openAccordionId: null
        };
    }

    handleAccordionToggle = (id) => {
        this.setState(prevState => ({
            openAccordionId: prevState.openAccordionId === id ? null : id
        }));
    };

    getErpFromUrl() {
        const searchParams = new URLSearchParams(window?.location?.search);
        return searchParams.get('erp');
    }

    getErpIconFromUrl() {
        const erp = getErpFromUrl();
        if (erp === "pohoda" || erp === "money-s3") {
            return erp + "-sign";
        }
        return erp;
    }

    getRealERPName = () => {
        let erp = getErpFromUrl();
        let erpMap = {
            "flexibee": "ABRA Flexi",
            "pohoda": "Pohoda",
            "money-s3": "Money S3",
            "abra-gen": "ABRA Gen",
        };
        return erpMap[erp];
    };

    filterFlows = (flow) => {
        const channel = this.props.user.organisation.channel;
        const flowChannels = flow.channels?.edges || [];
        const organisationChannels = this.props.user.organisation.channels?.edges || [];
        let selectedChannels = safeJSONparse(flow.selectedChannels);
        let hasEshop = false;
        let hasErp = false;

        // Control all channels in flow
        for (let channelId of Object.values(selectedChannels)) {
            // Control eshop from flow
            if (channelId === channel.realId) {
                hasEshop = true;
            }

            // Prefer flow channels so addon filtering is not limited by organisation.channels(first: 100).
            const channelInFlow = flowChannels.find(
                edge => edge?.node?.realId === channelId
            );
            const channelForErpMatch = channelInFlow || organisationChannels.find(
                edge => edge?.node?.realId === channelId
            );

            // Control if channelDef contains text from uiSettings.erp
            const erpFromSettings = resolveErp(getUISettings().erp)?.toLowerCase();
            const channelDefTypeLower = channelForErpMatch?.node?.channelDef?.type?.toLowerCase();

            // Normalize both strings for comparison: replace dots with dashes
            const normalizedChannelDefType = channelDefTypeLower?.replace(/\./g, '-');
            const normalizedErpFromSettings = erpFromSettings?.replace(/\./g, '-');

            const erpMatch = normalizedChannelDefType?.includes(normalizedErpFromSettings);

            if (erpMatch) {
                hasErp = true;
            }
        }

        return hasEshop && hasErp;
    };

    onApplyScenarioClick = (scenario) => {
        if (!this.props.user.organisation) {
            return null;
        }

        let path = urlMapping.getUrl(types["channels"], "scenario-apply", {
            organisation_id: this.props.user.organisation.realId,
            urlFragment: scenario.urlFragment,
            scenario_id: scenario.realId,
        });

        this.context.router.push(path);
    };

    getExistingFlow = (organisation, id) => {
        if (organisation.flows && organisation.flows.edges) {
            for (let flowEdge of organisation.flows.edges) {
                const flow = flowEdge.node;
                if (flow.scenario) {
                    if (flow.scenario.id === id) {
                        return flow;
                    }
                }
            }
        }
        return null;
    };

    getLastPathSegment() {
        const path = window.location.pathname;
        const segments = path.split("/");
        return segments[segments.length - 1];
    }

    getScenarioName(scenario) {
        return scenario.edges[0]?.node?.title;
    }

    render() {
        const erp = this.getRealERPName() || 'ABRA Flexi';

        const formatter = new Intl.NumberFormat('cs-CZ', {
            style: 'currency',
            currency: 'CZK',
            maximumFractionDigits: '0'
        });

        let intl = this.props.intl;

        const data = this.props.user.organisation.channel;
        let type = types[this.typeName];
        let app = this.getLastPathSegment();
        const addonPlatform = getAddonPlatform(app);
        let appName = addonPlatform.name;
        const addonParam = addonPlatform.addonParam;
        const invoiceScenarioUrl = getScenarioUrl(app, getErpFromUrl(), "invoice");
        const manualInvoiceScenarioUrl = getScenarioUrl(app, getErpFromUrl(), "invoice-manual");
        const orderScenarioUrl = getScenarioUrl(app, getErpFromUrl(), "order");
        const invoiceScenario = getScenarioNode(this.props.user.invoiceScenario);
        const orderScenario = getScenarioNode(this.props.user.orderScenario);
        const invoicePricing = getAddonPricingFromScenario(invoiceScenario, messages.transferInvoicesPricing, messages.transferInvoicesByCountPricing);
        const ordersFromErpPricing = getAddonPricingFromScenario(orderScenario, messages.transferOrdersPricing, messages.transferOrdersPricing, true);
        const ordersFromEshopPricing = getAddonPricingFromScenario(orderScenario, messages.transferOrders2Pricing, messages.transferOrders2Pricing, true);

        let possibleOptions = () => {
            const proposeSolutionOption = {
                title: intl.formatMessage(messages.proposeSolution, {appName, erp}),
                description: <span style={{fontStyle: 'italic'}}>{intl.formatMessage(messages.proposeSolutionDescription)}</span>,
                solution: intl.formatMessage(messages.proposeSolutionSolution),
                pricing: intl.formatMessage(messages.proposeSolutionPricing),
                showButtons: ["need-help"],
                icons: ["question-circle-o"],
            };

            if (!invoiceScenarioUrl && !orderScenarioUrl) {
                return [proposeSolutionOption];
            }

            return [
                false && app === "shoptet" && this.getErpFromUrl() === "flexibee" ? { // TODO: temporary disabled since manual invoices are not supported yet
                    title: intl.formatMessage(messages.manualTransferInvoices, {appName, erp}),
                    description: <span style={{fontStyle: 'italic'}}>{intl.formatMessage(varMessages.manualTransferInvoicesDescription, {appName, erp})}</span>,
                    solution: <SafeHTML value={intl.formatMessage(varMessages.manualTransferInvoicesSolution, {appName, erp, url: `${manualInvoiceScenarioUrl}/?${addonParam}`})} />,
                    pricing: intl.formatMessage(messages.manualTransferInvoicesPricing),
                    withDativeryPricing: 2000,
                    url: manualInvoiceScenarioUrl,
                    urlType: "invoices-manual",
                    icons: ["download"],
                } : undefined,
                invoiceScenarioUrl ? {
                    title: intl.formatMessage(messages.transferInvoices, {appName, erp}),
                    description: <span style={{fontStyle: 'italic'}}>{intl.formatMessage(varMessages.transferInvoicesDescription, {appName, erp})}</span>,
                    solution: <SafeHTML value={intl.formatMessage(varMessages.transferInvoicesSolution, {appName, erp, url: `${invoiceScenarioUrl}/?${addonParam}`})} />,
                    pricing: renderAddonPricing(intl, invoicePricing),
                    withDativeryPricing: invoicePricing.withDativeryPricing,
                    url: invoiceScenarioUrl,
                    urlType: "invoices",
                    icons: ["file-text"],
                } : undefined,
                orderScenarioUrl ? {
                    title: intl.formatMessage(messages.transferOrders, {erp}),
                    description: <span style={{fontStyle: 'italic'}}>{intl.formatMessage(varMessages.transferOrdersDescription, {appName, erp})}</span>,
                    solution: <SafeHTML value={intl.formatMessage(varMessages.transferOrdersSolution, {appName, erp, url: `${orderScenarioUrl}/?${addonParam}`})} />,
                    pricing: renderAddonPricing(intl, ordersFromErpPricing),
                    withDativeryPricing: ordersFromErpPricing.withDativeryPricing,
                    url: orderScenarioUrl,
                    urlType: "orders",
                    icons: ["file-text-o"],
                } : undefined,
                orderScenarioUrl ? {
                    title: intl.formatMessage(messages.transferOrders2, {appName, erp}),
                    description: <span style={{fontStyle: 'italic'}}>{intl.formatMessage(varMessages.transferOrders2Description, {appName, erp})}</span>,
                    solution: <SafeHTML value={intl.formatMessage(varMessages.transferOrdersSolution, {appName, erp, url: `${orderScenarioUrl}/?${addonParam}`})} />,
                    pricing: renderAddonPricing(intl, ordersFromEshopPricing),
                    withDativeryPricing: ordersFromEshopPricing.withDativeryPricing,
                    url: orderScenarioUrl,
                    urlType: "orders",
                    icons: ["shopping-cart"],
                } : undefined,
                proposeSolutionOption,
            ].filter((a) => !!a);
        };

        let createPossibleOptionsForInstall = (url) => {
            const baseUrl = urlMapping.getUrl(types["scenarios"], "scenario-apply-pre", { urlFragment: url || "" });
            const scenarioUrl = url ? `${baseUrl}?${addonParam}` : "";

            return [
                {
                    showType: "with-dativery",
                    header: intl.formatMessage(messages.withDativery),
                    description: intl.formatMessage(messages.withDativeryDescription),
                    pricing: intl.formatMessage(messages.withDativeryPricing),
                    scenarioUrl,
                },
                {
                    showType: "alone",
                    header: intl.formatMessage(messages.alone),
                    description: intl.formatMessage(messages.aloneDescription),
                    pricing: intl.formatMessage(messages.alonePricing),
                    scenarioUrl,
                },
                {
                    showType: "need-help",
                    header: intl.formatMessage(messages.needHelp),
                    description: intl.formatMessage(messages.needHelpDescription),
                    pricing: intl.formatMessage(messages.needHelpPricing),
                    scenarioUrl,
                }
            ];
        };
        return (
            <AppPanel
                breadcrumbParams={[
                    { type, data },
                    { type: types["organisations"], data: this.props.user.organisation },
                ]}
                className="ChannelView"
                title={intl.formatMessage(messages.title)}
            >
                <div style={{ minHeight: "85vh", backgroundColor: "#F6F6F6", padding: "4vh 4vh" }}>
                    <div
                        className="container"
                        style={{
                            width: "auto",
                            maxWidth: "1200px",
                            backgroundColor: "#fff",
                            borderRadius: "1rem",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.25)",
                        }}
                    >
                    <div className="row" style={{ marginBottom: "3rem" }}>
                        <div className="shoptet-application-detail__header">
                            <div className="shoptet-application-detail__icon-wrapper">
                                <ChannelIcon
                                    channelDef={{
                                        icon: addonPlatform.icon,
                                        name: config.PRODUCT_NAME,
                                    }}
                                    key={1}
                                    noDimensions
                                    size={32}
                                />
                            </div>
                            <i className="fa fa-long-arrow-right shoptet-application-detail__arrow" style={{ fontSize: "4rem" }}></i>
                            <div className="shoptet-application-detail__icon-wrapper">
                                <ChannelIcon
                                    channelDef={{
                                        icon: "dativery",
                                        name: config.PRODUCT_NAME,
                                    }}
                                    key={3}
                                    noDimensions
                                    size={48}
                                />
                            </div>
                            <i className="fa fa-long-arrow-right shoptet-application-detail__arrow" style={{ fontSize: "4rem" }}></i>
                            <div className="shoptet-application-detail__icon-wrapper">
                                <ChannelIcon
                                    channelDef={{
                                        icon: getErpIconFromUrl(),
                                        name: config.PRODUCT_NAME,
                                    }}
                                    key={2}
                                    noDimensions
                                    size={32}
                                />
                            </div>
                        </div>
                            <div style={{ width: "100%", display: "flex", flexDirection: "column", padding: "4rem" }}>

                                <div style={{ textAlign: "center", margin: "0" }}>
                                    <h2>
                                        <b>{intl.formatMessage(messages.welcome)}</b>
                                    </h2>
                                    <p style={{ fontSize: "20px", opacity: "0.85" }}>
                                        {intl.formatMessage(messages.welcomeDescription, {appName, erp})}
                                    </p>
                                </div>
                                <h3>{intl.formatMessage(messages.whatDoYouExpect, {erp, appName})}</h3>
                                <div style={{ width: "100%", display: "flex", flexDirection: "column", marginBottom: "30px" }}>
                                    {possibleOptions().map((i, index) => (
                                        <CustomAccordion
                                            icons={i.icons}
                                            isOpen={this.state.openAccordionId === index}
                                            key={i.title}
                                            onToggle={() => this.handleAccordionToggle(index)}
                                            title={i.title}
                                        >
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: "1.5rem" }}>
                                                <div style={{ display: "flex", flexDirection: "column", width: "100%", marginBottom: "2rem" }}>
                                                    <p style={{ fontSize: "16px", marginBottom: "2rem" }}>{i.description}</p>
                                                    <p style={{ fontSize: "16px" }}>
                                                        <b>{intl.formatMessage(messages.solution)}:</b> {i.solution}
                                                    </p>
                                                    <p style={{ fontSize: "16px" }}>
                                                        <b>{intl.formatMessage(messages.price)}:</b> {i.pricing}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="shoptet-application-detail__options-container">
                                            {createPossibleOptionsForInstall(i.url).map(
                                                    (box, idx) =>
                                                        (i.showButtons || ["with-dativery", "need-help", "alone"]).includes(box.showType) && (
                                                            <div className="shoptet-application-detail__btn-box" key={`${box.showType}-${idx}`}>
                                                            <TooltipButton
                                                                    className={`${idx !== 0 ? "shoptet-application-detail__tooltip-button--light" : "shoptet-application-detail__tooltip-button"}`}
                                                                    key={idx}
                                                                    onClick={(e) => {
                                                                    let props = {
                                                                        showType: box.showType,
                                                                        scenarioUrl: box.scenarioUrl,
                                                                        scenarioName: i.urlType ? (i.urlType === "orders" ? this.getScenarioName(this.props.user.orderScenario) : this.getScenarioName(this.props.user.invoiceScenario)) : "Chci navrhnout řešení",
                                                                        email: this.props.user.organisation.billingEmail || this.props.user.organisation.email,
                                                                        name: this.props.user.organisation.billingName,
                                                                        companyName: this.props.user.organisation.name,
                                                                        phone: this.props.user.organisation.phoneNumber,
                                                                        typeOfInstall: box.header,
                                                                        erp: erp,
                                                                        appName: appName,
                                                                    };
                                                                    InquiryModal.show(props);
                                                                }}
                                                                style={{ width: "100%" }}
                                                                tooltipText={box.description}
                                                            >
                                                                <div className="shoptet-application-detail__btn-title">
                                                                    {idx === 0 ? <b>{box.header}</b> : box.header}
                                                                </div>
                                                                <span className="shoptet-application-detail__btn-price">
                                                                {(() => {
                                                                    const price = box.showType === "with-dativery" ? i.withDativeryPricing : box.pricing;
                                                                    const numericPrice = parseFloat(price);
                                                                    if (box.showType === "with-dativery" && (!numericPrice || isNaN(numericPrice))) {
                                                                        return null;
                                                                    }
                                                                    return !isNaN(numericPrice) && numericPrice !== 0 ? (
                                                                            <span>
                                                                                {formatter.format(numericPrice)}
                                                                            </span>
                                                                        ) : <span>{box.pricing}</span>;
                                                                    })()}
                                                                </span>
                                                            </TooltipButton>
                                                            </div>
                                                        )
                                                )}
                                            </div>
                                        </CustomAccordion>
                                    ))}
                                </div><FlowSimpleList filter={this.filterFlows} organisation={this.props.user.organisation} />
                            </div>
                        </div>
                    </div>
                </div>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(
    wrapNotFound(injectIntl(ShoptetApplicationDetail), (props) => {
        return props.user.organisation && props.user.organisation.channel;
    }),
    {
        initialVariables: {
            organisationId: "",
            channelId: "",
            scenarioUrlFragment: "",
            urlFragmentOrders: "",
            urlFragmentInvoices: "",
        },
        prepareVariables(variables) {
            if (!variables.organisationId) {
                variables.organisationId = getLastOrganisationId();
            }

             // Extract the last segment of the URL
             const path = window.location.pathname;
             const segments = path.split("/");
             const app = segments[segments.length - 1];

             // Determine the appropriate URL fragments based on the last segment
             variables.urlFragmentOrders = getScenarioUrl(app, getErpFromUrl(), "order") || NO_ADDON_SCENARIO_URL_FRAGMENT;
             variables.urlFragmentInvoices = getScenarioUrl(app, getErpFromUrl(), "invoice") || NO_ADDON_SCENARIO_URL_FRAGMENT;
             return variables;
        },
        fragments: {
            user: () => Relay.QL`
			fragment on User {
				featureFlags,
                orderScenario: publicScenarios(first: 1, urlFragment: $urlFragmentOrders) {
                    edges {
                        node {
                            id,
                            realId,
                            name,
                            title,
                            description,
                            createdAt,
                            updatedAt,
                            urlFragment,
                            billingType,
                            priceInstallation,
                            priceSelfInstallation,
                            pricePerInstance,
                            pricePerInstanceNext,
                            pricePerInvocation,
                            priceText,
                            invocationName
                        }
                    }
                },
                invoiceScenario: publicScenarios(first: 1, urlFragment: $urlFragmentInvoices) {
                    edges {
                        node {
                            id,
                            realId,
                            name,
                            title,
                            description,
                            createdAt,
                            updatedAt,
                            urlFragment,
                            billingType,
                            priceInstallation,
                            priceSelfInstallation,
                            pricePerInstance,
                            pricePerInstanceNext,
                            pricePerInvocation,
                            priceText,
                            invocationName
                        }
                    }
                },
				organisation(id: $organisationId) {
					id,
					realId,
					name,
					featureFlags,
					billingEmail,
					email,
					billingCurrency,
					billingName,
					phoneNumber,
					channels(first: 100) {
						edges {
							node {
								id,
								realId,
								type,
								channelDef {
									id,
									realId,
									name,
									type
								}
							}
						}
					},
					channel(id: $channelId) {
						id,
						realId,
						organisation_id,
						type,
						description,
						name,
						metadata,
						channelDef {
							id,
							name,
							description,
							metadata,
							icon,
                            type
						},
					},
					${FlowSimpleList.getFragment("organisation")},
				}
			}
		`,
        },
    }
);
